import React from 'react';
import { Column } from 'react-table';
import moment from 'moment-timezone';

import {
  IEpxDepress,
  IEpxDepressPHQ,
  IEpxDepressGAD,
  IEpxDepressCOVID,
  getPHQAnswer,
  getPHQ2Total,
  isPHQ2Escalated,
  getGADAnswer
} from '../../../../../libs/apps/epxdepress';
import { ISession } from '../../../../../libs/session';

import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  YesNoCell,
  TimeCell,
  DurationCell,
  CellFunction,
  NoneGuard
} from '../../cells';
import Tooltip from '../../../tooltip';

const MoodCell: CellFunction<IEpxDepress> = ({
  row: { original: session }
}) => {
  const rating = session.mood as number;
  const invalidated = session?.invalidation?.key === 'mood';
  const moodNewValue = session?.invalidation?.newValue;
  const invalidationDate = session?.invalidation?.createdAt
    ? moment(session.invalidation.createdAt).format('MMM D, YYYY h:mm A')
    : null;
  const prevValue = session?.invalidation?.previousValue;
  if (invalidated) {
    const tooltipText = `This mood score was invalidated on ${
      invalidationDate || 'unknown date'
    } and the Original value was ${prevValue ?? '?'}/10`;
    return (
      <Tooltip content={tooltipText} position="top">
        <span>{moodNewValue != null ? `${moodNewValue}/10` : '?'}*</span>
      </Tooltip>
    );
  } else if (rating != null) {
    return <span>{rating}/10</span>;
  }
  return NoneGuard(rating);
};

const EpxDepressTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxDepress>[] = [
    {
      Header: 'Mood',
      id: 'mood',
      Cell: ResponseGuard(MoodCell)
    },
    {
      Header: 'Worthless Feeling',
      id: 'suicideTrend',
      Cell: ResponseGuard(
        YesNoCell('suicideTrend') as CellFunction<IEpxDepress>
      )
    },
    {
      Header: 'Suicide Thought',
      id: 'suicide',
      Cell: ResponseGuard(YesNoCell('suicide') as CellFunction<IEpxDepress>)
    }
  ];
  return (
    <SessionTable<IEpxDepress>
      url="/epxdepress/data/"
      columns={columns}
      appKey="epxdepress"
      invalidationModel="EpxDepress"
      {...props}
    />
  );
};

export default EpxDepressTable;

// PHQ-9

const PHQ9 = [
  'Have you had little interest or pleasure in doing things?',
  'How often have you felt down, depressed, or hopeless?',
  'How often have you had trouble sleeping or slept too much?',
  'How often have you felt tired or had little energy?',
  'How often have you had poor appetite or eaten too much?',
  'How often have you felt like a failure?',
  'How often have you had trouble concentrating?',
  'Have others noticed you moving or speaking slowly? Being fidgety or restless?',
  'How often have you thought you would be better off dead or of hurting yourself?'
];

const PHQResponseTimeCell = ({
  row: { original: session }
}): CellFunction<IEpxDepress> => {
  const answers = session.answers || {};
  if (!(answers && answers[1] != null)) {
    return <span>Never</span>;
  }
  const value = session.updatedAt as Date;
  const time = moment(value);
  const format = 'MM/DD/YY hh:mm a';
  return NoneGuard(value) || <span>{time.format(format)}</span>;
};

const PHQCell = (index: number): CellFunction<IEpxDepressPHQ> => {
  return ({ row: { original: session } }) => {
    const answer = getPHQAnswer(session, index);
    const answerValue = answer?.value ?? null;
    const intensity = answerValue == null ? 0 : (answerValue + 1) * 25;

    if (answer?.invalidated) {
      const tooltipText = `This answer was invalidated on ${
        answer.invalidationDate || 'unknown date'
      } and the Original value was ${answer.prevValue ?? '?'}`;
      return (
        <div className={`value--${intensity} font-bold`}>
          <Tooltip content={tooltipText} position="top">
            <span>{answerValue != null ? answerValue : '?'}*</span>
          </Tooltip>
        </div>
      );
    }
    return (
      <div className={`value--${intensity} font-bold`}>
        <Tooltip
          content={
            PHQ9[index - 1] + ' Scale: 0 (Not At All) – 3 (Almost Daily)'
          }
          position="top"
        >
          <span>{answerValue != null ? answerValue : '--'}</span>
        </Tooltip>
      </div>
    );
  };
};

const PHQTotalCell: CellFunction<IEpxDepressPHQ> = ({
  row: { original: session }
}) => {
  const answers = session.answers || {};
  let total = 0;
  for (let i = 0; i < 9; i++) {
    if (answers[i + 1] >= 0) {
      total += answers[i + 1];
    }
  }
  return (
    <Tooltip
      content={
        <span>
          PHQ-9 Score Interpretation:
          <br />
          0-4: Minimal
          <br />
          5-9: Mild
          <br />
          10-14: Moderate
          <br />
          15-19: Moderately Severe
          <br />
          20-27: Severe
          <br />
        </span>
      }
    >
      <b>{total}</b>
    </Tooltip>
  );
};

export const EpxDepressPHQTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxDepressPHQ>[] = [
    {
      Header: 'Survey Number',
      accessor: 'serial',
      Cell: ({ cell: { value } }) => {
        return <b>#{value + 1}</b>;
      }
    },
    {
      Header: 'Time Window',
      accessor: 'time',
      Cell: ({ cell: { value } }) => {
        const start = moment(value as Date);
        const end = start.clone().add(7, 'days');
        return (
          <span>
            {start.format('MM/DD')} - {end.format('MM/DD')}
          </span>
        );
      }
    },
    {
      Header: 'Last Response At',
      id: 'lastResponseAt',
      Cell: PHQResponseTimeCell as CellFunction<IEpxDepressPHQ>
    }
  ];
  columns.push({
    Header: 'PHQ-9 Total',
    id: 'total',
    Cell: PHQTotalCell
  });
  for (let i = 0; i < 9; i++) {
    columns.push({
      Header: PHQ9[i],
      id: `q${i + 1}`,
      Cell: PHQCell(i + 1)
    });
  }
  return (
    <SessionTable<IEpxDepressPHQ>
      url="/epxdepress/data_phq/"
      type="vertical"
      columns={columns}
      disableDefaultColumns
      appKey="epxdepressphq"
      invalidationModel="EpxDepressPHQ"
      {...props}
      striped={false}
    />
  );
};

// PHQ-2

const PHQ2TotalCell: CellFunction<IEpxDepressPHQ> = ({
  row: { original: session }
}) => {
  const total = getPHQ2Total(session);
  return (
    <Tooltip
      content={
        <span>
          PHQ-2 Score Interpretation:
          <br />
          0-2: Depression unlikely
          <br />
          3-6: Further evaluation recommended (PHQ-9)
          <br />
        </span>
      }
    >
      <b>{total != null ? total : '--'}</b>
    </Tooltip>
  );
};

const PHQ2EscalatedCell: CellFunction<IEpxDepressPHQ> = ({
  row: { original: session }
}) => {
  const total = getPHQ2Total(session);
  if (total == null) return <span>--</span>;
  const escalated = isPHQ2Escalated(session);
  return (
    <Tooltip
      content={
        escalated
          ? 'PHQ-2 score \u2265 3: Patient continued to full PHQ-9'
          : 'PHQ-2 score < 3: No further screening needed'
      }
      position="top"
    >
      <span className={escalated ? 'text-warning font-bold' : ''}>
        {escalated ? 'Yes' : 'No'}
      </span>
    </Tooltip>
  );
};

export const EpxDepressPHQ2Table: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxDepressPHQ>[] = [
    {
      Header: 'Survey Number',
      accessor: 'serial',
      Cell: ({ cell: { value } }) => {
        return <b>#{value + 1}</b>;
      }
    },
    {
      Header: 'Time Window',
      accessor: 'time',
      Cell: ({ cell: { value } }) => {
        const start = moment(value as Date);
        const end = start.clone().add(7, 'days');
        return (
          <span>
            {start.format('MM/DD')} - {end.format('MM/DD')}
          </span>
        );
      }
    },
    {
      Header: 'Last Response At',
      id: 'lastResponseAt',
      Cell: PHQResponseTimeCell as CellFunction<IEpxDepressPHQ>
    }
  ];
  columns.push({
    Header: 'PHQ-2 Total',
    id: 'phq2Total',
    Cell: PHQ2TotalCell
  });
  // Only Q1 and Q2
  for (let i = 0; i < 2; i++) {
    columns.push({
      Header: PHQ9[i],
      id: `q${i + 1}`,
      Cell: PHQCell(i + 1)
    });
  }
  columns.push({
    Header: 'Escalated to PHQ-9',
    id: 'escalated',
    Cell: PHQ2EscalatedCell
  });
  return (
    <SessionTable<IEpxDepressPHQ>
      url="/epxdepress/data_phq/"
      type="vertical"
      columns={columns}
      disableDefaultColumns
      appKey="epxdepressphq"
      invalidationModel="EpxDepressPHQ"
      {...props}
      striped={false}
    />
  );
};

// PHQ-2 → PHQ-9

const PHQ2SubtotalCell: CellFunction<IEpxDepressPHQ> = ({
  row: { original: session }
}) => {
  const total = getPHQ2Total(session);
  return (
    <Tooltip
      content="Sum of Q1 + Q2 (PHQ-2 screening score). Score \u2265 3 triggers full PHQ-9."
      position="top"
    >
      <b>{total != null ? total : '--'}</b>
    </Tooltip>
  );
};

export const EpxDepressPHQ2ToPHQ9Table: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxDepressPHQ>[] = [
    {
      Header: 'Survey Number',
      accessor: 'serial',
      Cell: ({ cell: { value } }) => {
        return <b>#{value + 1}</b>;
      }
    },
    {
      Header: 'Time Window',
      accessor: 'time',
      Cell: ({ cell: { value } }) => {
        const start = moment(value as Date);
        const end = start.clone().add(7, 'days');
        return (
          <span>
            {start.format('MM/DD')} - {end.format('MM/DD')}
          </span>
        );
      }
    },
    {
      Header: 'Last Response At',
      id: 'lastResponseAt',
      Cell: PHQResponseTimeCell as CellFunction<IEpxDepressPHQ>
    }
  ];
  // PHQ-2 subtotal first
  columns.push({
    Header: 'PHQ-2 Subtotal (Q1+Q2)',
    id: 'phq2Subtotal',
    Cell: PHQ2SubtotalCell
  });
  // Full PHQ-9 total
  columns.push({
    Header: 'PHQ-9 Total',
    id: 'total',
    Cell: PHQTotalCell
  });
  // Escalated indicator
  columns.push({
    Header: 'Escalated to PHQ-9',
    id: 'escalated',
    Cell: PHQ2EscalatedCell
  });
  // All 9 questions
  for (let i = 0; i < 9; i++) {
    columns.push({
      Header: PHQ9[i],
      id: `q${i + 1}`,
      Cell: PHQCell(i + 1)
    });
  }
  return (
    <SessionTable<IEpxDepressPHQ>
      url="/epxdepress/data_phq/"
      type="vertical"
      columns={columns}
      disableDefaultColumns
      appKey="epxdepressphq"
      invalidationModel="EpxDepressPHQ"
      {...props}
      striped={false}
    />
  );
};

// GAD-7

const GAD7 = [
  'Feeling nervous, anxious or on edge?',
  'Not being able to stop or control worrying?',
  'Worrying too much about different things?',
  'Trouble relaxing?',
  'Being so restless that it is hard to sit still?',
  'Becoming easily annoyed or irritable?',
  'Feeling afraid as if something awful might happen?'
];

const GADCell = (index: number): CellFunction<IEpxDepressGAD> => {
  return ({ row: { original: session } }) => {
    const answer = getGADAnswer(session, index);
    const answerValue = answer?.value ?? null;

    if (answer?.invalidated) {
      const tooltipText = `This answer was invalidated on ${
        answer.invalidationDate || 'unknown date'
      } and the Original value was ${answer.prevValue ?? '?'}`;
      return (
        <Tooltip content={tooltipText} position="top">
          <span>{answerValue != null ? answerValue : '?'}*</span>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        content={
          GAD7[index - 1] +
          ' Scale: 0 (Not at All), 1 (Several Days), 2 (More than Half the Days), 3 (Nearly Everyday)'
        }
        position="top"
      >
        <span>{answerValue != null ? answerValue : '--'}</span>
      </Tooltip>
    );
  };
};

const GADTotalCell: CellFunction<IEpxDepressGAD> = ({
  row: { original: session }
}) => {
  let total = 0;
  for (let i = 0; i < 9; i++) {
    if (session[`q${i + 1}`] >= 0) {
      total += session[`q${i + 1}`];
    }
  }
  return <span>{total}</span>;
};

const GADImpactCell: CellFunction<IEpxDepressGAD> = ({
  row: { original: session }
}) => {
  return (
    NoneGuard(session.impact) || (
      <Tooltip content="How difficult have these made it for you to do your work, take care of things at home, or get along with other people? Scale: 0 (Not At All), 1 (Somewhat), 2 (Very), 3 (Extremely)">
        <span>{session.impact}</span>
      </Tooltip>
    )
  );
};

export const EpxDepressGADTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxDepressGAD>[] = [
    {
      Header: 'Survey Number',
      accessor: 'serial',
      Cell: ({ cell: { value } }) => {
        return <b>#{value + 1}</b>;
      }
    },
    {
      Header: 'GAD-7 Date',
      Cell: TimeCell('time', 'MM/DD/YYYY')
    }
  ];
  for (let i = 0; i < 7; i++) {
    columns.push({
      Header: GAD7[i],
      id: `q${i + 1}`,
      Cell: GADCell(i + 1)
    });
  }
  columns.push({
    Header: 'GAD-7 Impact Question',
    id: 'impact',
    Cell: GADImpactCell
  });
  columns.push({
    Header: 'GAD-7 Total',
    id: 'total',
    Cell: GADTotalCell
  });
  return (
    <SessionTable<IEpxDepressGAD>
      url="/epxdepress/data_gad/"
      type="vertical"
      columns={columns}
      disableDefaultColumns
      appKey="epxdepressgad"
      invalidationModel="EpxDepressGAD"
      {...props}
    />
  );
};

// COVID PHQ-4

const PHQ4 = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed or hopeless',
  'Feeling nervous, anxious or on edge',
  'Not being able to stop or control worrying'
];

const PHQ4Cell = (index: number): CellFunction<IEpxDepressCOVID> => {
  return ({ row: { original: session } }) => {
    const answer = session[`phq4Q${index}`];
    return (
      NoneGuard(answer) || (
        <Tooltip
          content={
            PHQ4[index - 1] +
            ' Scale: 0 (Not at All), 1 (Several Days), 2 (More than Half the Days), 3 (Nearly Everyday)'
          }
        >
          <span>{answer}</span>
        </Tooltip>
      )
    );
  };
};

const PHQ4TotalCell: CellFunction<IEpxDepressCOVID> = ({
  row: { original: session }
}) => {
  return NoneGuard(session.phq4Total) || <span>{session.phq4Total}</span>;
};

const PHQ4WorseCell: CellFunction<IEpxDepressCOVID> = ({
  row: { original: session }
}) => {
  return (
    NoneGuard(session.isWorsenedAfterActivity) || (
      <Tooltip content="Do the things that bother you get worse after physical or mental activities?">
        <span>{session.isWorsenedAfterActivity ? 'Yes' : 'No'}</span>
      </Tooltip>
    )
  );
};

export const EpxDepressCOVIDTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const columns: Column<IEpxDepressCOVID>[] = [
    {
      Header: 'PHQ-4 Date',
      Cell: TimeCell('time', 'MM/DD/YYYY')
    }
  ];
  for (let i = 0; i < 4; i++) {
    columns.push({
      Header: PHQ4[i],
      id: `q${i + 1}`,
      Cell: PHQ4Cell(i + 1) as CellFunction<IEpxDepressCOVID>
    });
  }
  columns.push({
    Header: 'PHQ-4 Total',
    id: 'total',
    Cell: PHQ4TotalCell as CellFunction<IEpxDepressCOVID>
  });
  columns.push({
    Header: 'Worse After Activities',
    id: 'impact',
    Cell: PHQ4WorseCell as CellFunction<IEpxDepressCOVID>
  });
  return (
    <SessionTable<IEpxDepressCOVID>
      url="/epxdepress/data_covid/"
      type="vertical"
      columns={columns}
      disableDefaultColumns
      {...props}
    />
  );
};

interface SuicideConnect extends ISession {
  eta: Date;
  duration: number;
}

export const EpxDepressSuicideConnectTable: React.FC<
  SessionTableInstanceProps
> = (props): JSX.Element => {
  const columns: Column<SuicideConnect>[] = [
    {
      Header: 'Time',
      Cell: TimeCell('eta', 'MM/DD/YYYY hh:mmA')
    },
    {
      Header: 'Duration',
      id: 'duration',
      Cell: DurationCell('duration')
    }
  ];
  return (
    <SessionTable<SuicideConnect>
      url="/epxdepress/data_suicide_connect/"
      defaultLimit={5}
      columns={columns}
      disableDefaultColumns
      disableControls
      {...props}
    />
  );
};
